import { useEffect, useMemo, useState } from 'react'
import {
  Bot,
  Building2,
  Check,
  Edit,
  Eye,
  GitBranch,
  Loader2,
  MoreVertical,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  ArrowRightLeft,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { useManagerNavigation } from '@/components/manager/useManagerNavigation'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  useCompanyPhoneNumbers,
  type AssignmentType,
  type CompanyPhoneNumber,
} from '@/api/company'
import {
  formatPhoneNumber,
  useUpdatePhoneNumber,
} from '@/api/phoneNumbers'
import { usePortRequests, type PortNumber, type PortRequest } from '@/api/portage'
import { NumberMarketplaceDialog } from './NumberMarketplaceDialog'
import { TollfreeSmsPill } from './TollfreeSms'
import { StatusBadge } from './portage/PortRequestDetailView'
import { formatNanp, formatShortDay, pendingNumbers } from './portage/portageUtils'

type FilterType = 'all' | AssignmentType | 'ivr_flow' | 'transfers'

interface Props {
  companyId: number
  onSelect: (num: CompanyPhoneNumber) => void
  onSelectTransfer: (transfer: PortRequest) => void
  onStartTransfer: () => void
}

/** One line of the table for a number still on its way (a draft without numbers gets one too). */
interface TransferLine {
  key: string
  transfer: PortRequest
  number: PortNumber | null
}

export function NumbersListView({ companyId, onSelect, onSelectTransfer, onStartTransfer }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocaleStr = locale === 'fr' ? 'fr-CA' : 'en-CA'

  const { data, isLoading } = useCompanyPhoneNumbers(companyId)
  const { data: ports } = usePortRequests(companyId)
  const { setActiveTab } = useManagerNavigation()
  const [marketOpen, setMarketOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')

  const numbers = data?.phone_numbers ?? []
  const stats = data?.stats
  const transfers = useMemo(() => ports?.transfers ?? [], [ports])

  // The Callizy number used during a transfer, and the ported numbers already set up in Callizy.
  const tagsByNumberId = useMemo(() => {
    const tags = new Map<number, string[]>()
    const add = (id: number | null | undefined, tag: string) => {
      if (!id) return
      tags.set(id, [...(tags.get(id) ?? []), tag])
    }
    transfers
      .filter((tr) => tr.status !== 'brouillon')
      .forEach((tr) => {
        add(tr.temporary_number?.id, t('manager.numbers.port.list.temporary'))
        tr.numbers.forEach((n) => n.status === 'branche' && add(n.phone_number_id, t('manager.numbers.port.list.type')))
      })
    return tags
  }, [transfers, t])

  const transferLines = useMemo<TransferLine[]>(() => {
    if (filterType !== 'all' && filterType !== 'transfers') return []
    const q = searchQuery.replace(/\D/g, '')
    return transfers
      .flatMap((tr): TransferLine[] => {
        if (tr.numbers.length === 0) return [{ key: tr.id, transfer: tr, number: null }]
        return pendingNumbers(tr).map((n) => ({ key: `${tr.id}-${n.id}`, transfer: tr, number: n }))
      })
      .filter((l) => !searchQuery || (!!q && (l.number?.phone_number ?? '').includes(q)))
  }, [transfers, filterType, searchQuery])

  const filteredNumbers = useMemo(() => {
    if (filterType === 'transfers') return []
    return numbers.filter((n) => {
      // Filtre type
      if (filterType !== 'all') {
        const effective = n.ivr_flow_id ? 'ivr_flow' : n.assignment_type
        if (filterType === 'ivr_flow') {
          if (effective !== 'ivr_flow') return false
        } else if (filterType !== effective) return false
      }
      // Filtre recherche (sur phone_number et label)
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const hayphone = n.phone_number.toLowerCase()
        const haylabel = (n.label ?? '').toLowerCase()
        if (!hayphone.includes(q) && !haylabel.includes(q)) return false
      }
      return true
    })
  }, [numbers, filterType, searchQuery])

  return (
    <div className="space-y-0">
      {/* Title row */}
      <div className="flex flex-wrap items-start justify-between gap-4 pb-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {t('manager.numbers.sectionTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.numbers.sectionSubtitle')}
          </p>
        </div>
      </div>

      {/* Search bar + filter + buy button */}
      <div className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Search className="h-4 w-4" />
            </div>
            <input
              type="text"
              placeholder={t('manager.numbers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent border border-slate-300 dark:border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-slate-400 dark:placeholder-slate-500 transition-shadow"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <span>{t(`manager.numbers.filter.${filterType}`)}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem
                onClick={() => setFilterType('all')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.all')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilterType('main')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.main')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilterType('ai_agent')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.ai_agent')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilterType('ivr_flow')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.ivr_flow')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilterType('unassigned')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.unassigned')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setFilterType('transfers')}
                className="text-sm cursor-pointer"
              >
                {t('manager.numbers.filter.transfers')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={() => setActiveTab('trust')}>
            <ShieldCheck className="h-4 w-4 mr-2" />
            {t('manager.trust.title')}
          </Button>
          <Button type="button" variant="outline" onClick={onStartTransfer}>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            {t('manager.numbers.port.cta')}
          </Button>
          <Button
            type="button"
            onClick={() => setMarketOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('manager.numbers.buyCta')}
          </Button>
        </div>
      </div>

      {/* Stats line */}
      {stats && (
        <div className="pb-4">
          <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
            {t('manager.numbers.totalCount', { count: stats.total })}
            {stats.main > 0 &&
              ` · ${t('manager.numbers.includedInPlan', { count: stats.main })}`}
          </p>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wider">
              <th className="px-6 py-4">
                {t('manager.numbers.table.number')}
              </th>
              <th className="px-6 py-4">{t('manager.numbers.table.type')}</th>
              <th className="px-6 py-4">{t('manager.numbers.table.owner')}</th>
              <th className="px-6 py-4">
                {t('manager.numbers.table.capabilities')}
              </th>
              <th className="px-6 py-4">
                {t('manager.numbers.table.purchaseDate')}
              </th>
              <th className="px-6 py-4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                </td>
              </tr>
            ) : filteredNumbers.length === 0 && transferLines.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-slate-500">
                  <div className="flex flex-col items-center gap-3">
                    <Phone className="h-6 w-6 text-slate-300" />
                    <span>{t('manager.numbers.emptyState')}</span>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {transferLines.map((line) => (
                  <TransferRow
                    key={line.key}
                    line={line}
                    locale={locale}
                    onSelect={() => onSelectTransfer(line.transfer)}
                  />
                ))}
                {filteredNumbers.map((number, index) => (
                  <DesktopRow
                    key={number.id}
                    number={number}
                    isIncluded={index === 0}
                    tags={tagsByNumberId.get(number.id)}
                    companyId={companyId}
                    dateLocale={dateLocaleStr}
                    onSelect={() => onSelect(number)}
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-800">
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : filteredNumbers.length === 0 && transferLines.length === 0 ? (
          <div className="text-center py-16 text-slate-500 flex flex-col items-center gap-3">
            <Phone className="h-6 w-6 text-slate-300" />
            <span className="text-sm">{t('manager.numbers.emptyState')}</span>
          </div>
        ) : (
          <>
            {transferLines.map((line) => (
              <TransferCard
                key={line.key}
                line={line}
                locale={locale}
                onSelect={() => onSelectTransfer(line.transfer)}
              />
            ))}
            {filteredNumbers.map((number, index) => (
              <MobileCard
                key={number.id}
                number={number}
                isIncluded={index === 0}
                tags={tagsByNumberId.get(number.id)}
                companyId={companyId}
                dateLocale={dateLocaleStr}
                onSelect={() => onSelect(number)}
              />
            ))}
          </>
        )}
      </div>

      <NumberMarketplaceDialog
        open={marketOpen}
        onClose={() => setMarketOpen(false)}
        companyId={companyId}
      />
    </div>
  )
}

// =============================================================================
// Sous-composants
// =============================================================================

interface RowProps {
  number: CompanyPhoneNumber
  isIncluded: boolean
  /** Short labels next to the number (temporary number of a transfer, ported number on its way). */
  tags?: string[]
  companyId: number
  dateLocale: string
  onSelect: () => void
}

function DesktopRow({
  number,
  isIncluded,
  tags,
  companyId,
  dateLocale,
  onSelect,
}: RowProps) {
  const [editingLabel, setEditingLabel] = useState(false)
  const purchased = new Date(number.purchased_at).toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <tr
      onClick={editingLabel ? undefined : onSelect}
      className={cn(
        'transition-colors',
        editingLabel
          ? 'bg-blue-50/40 dark:bg-blue-500/5'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer',
      )}
    >
      <td className="px-6 py-4">
        <NumberCell
          number={number}
          isIncluded={isIncluded}
          tags={tags}
          companyId={companyId}
          editing={editingLabel}
          onStopEdit={() => setEditingLabel(false)}
        />
      </td>
      <td className="px-6 py-4">
        <AssignmentBadge number={number} />
      </td>
      <td className="px-6 py-4">
        <OwnerCell number={number} />
      </td>
      <td className="px-6 py-4">
        <CapabilitiesCell number={number} />
      </td>
      <td className="px-6 py-4 text-slate-500 dark:text-slate-400">
        {purchased}
      </td>
      <td
        className="px-6 py-4 text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <RowActions
          number={number}
          companyId={companyId}
          onDetails={onSelect}
          onEditLabel={() => setEditingLabel(true)}
        />
      </td>
    </tr>
  )
}

function MobileCard({
  number,
  isIncluded,
  tags,
  companyId,
  dateLocale,
  onSelect,
}: RowProps) {
  const [editingLabel, setEditingLabel] = useState(false)
  const purchased = new Date(number.purchased_at).toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <div
      onClick={editingLabel ? undefined : onSelect}
      className={cn(
        'p-4 transition-colors',
        editingLabel
          ? 'bg-blue-50/40 dark:bg-blue-500/5'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer',
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <NumberCell
            number={number}
            isIncluded={isIncluded}
            tags={tags}
            companyId={companyId}
            editing={editingLabel}
            onStopEdit={() => setEditingLabel(false)}
          />
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <RowActions
            number={number}
            companyId={companyId}
            onDetails={onSelect}
            onEditLabel={() => setEditingLabel(true)}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <AssignmentBadge number={number} />
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <CapabilitiesCell number={number} />
        <span>{purchased}</span>
      </div>
    </div>
  )
}

interface NumberCellProps {
  number: CompanyPhoneNumber
  isIncluded: boolean
  tags?: string[]
  companyId: number
  editing: boolean
  onStopEdit: () => void
}

function NumberCell({
  number,
  isIncluded,
  tags,
  companyId,
  editing,
  onStopEdit,
}: NumberCellProps) {
  const { t } = useTranslation()
  const update = useUpdatePhoneNumber(companyId, number.id)
  const [draft, setDraft] = useState(number.label ?? '')

  // Resync le draft quand on (re)passe en mode édition ou que le label change côté serveur.
  useEffect(() => {
    if (editing) setDraft(number.label ?? '')
  }, [editing, number.label])

  const handleSave = async () => {
    const trimmed = draft.trim()
    if (trimmed === (number.label ?? '')) {
      onStopEdit()
      return
    }
    try {
      await update.mutateAsync({ label: trimmed })
      toast.success(t('manager.numbers.detail.labelSaved'))
      onStopEdit()
    } catch {
      toast.error(t('manager.numbers.detail.labelError'))
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900 dark:text-white">
          {formatPhoneNumber(number.phone_number)}
        </span>
        {isIncluded && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800/50">
            {t('manager.numbers.included')}
          </span>
        )}
        {tags?.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50"
          >
            {tag}
          </span>
        ))}
      </div>
      {editing ? (
        <div
          className="flex items-center gap-1 mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave()
              if (e.key === 'Escape') {
                setDraft(number.label ?? '')
                onStopEdit()
              }
            }}
            placeholder={t('manager.numbers.labelInlinePlaceholder')}
            className="text-xs px-2 py-1 rounded border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40 w-48"
            autoFocus
          />
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={update.isPending}
            className="p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
          >
            {update.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraft(number.label ?? '')
              onStopEdit()
            }}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <span className="text-xs text-slate-500 mt-0.5">
          {number.label || '—'}
        </span>
      )}
    </div>
  )
}

function TransferDateText({ transfer, locale }: { transfer: PortRequest; locale: string }) {
  const { t } = useTranslation()
  // The carrier sets the date: nothing to show before it is confirmed.
  if (transfer.status === 'brouillon') return <>{t('manager.numbers.port.list.toFinish')}</>
  if (!transfer.confirmed_date) return <>{t('manager.numbers.port.list.dateToConfirm')}</>
  return <>{t('manager.numbers.port.list.planned', { date: formatShortDay(transfer.confirmed_date, locale) })}</>
}

function TransferTypeBadge() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/20">
      <ArrowRightLeft className="h-3 w-3" />
      {t('manager.numbers.port.list.type')}
    </span>
  )
}

function transferFrom(line: TransferLine, t: (k: string, o?: Record<string, unknown>) => string) {
  const carrier = line.number?.carrier || line.transfer.carriers[0]
  return carrier ? t('manager.numbers.port.list.from', { carrier }) : t('manager.numbers.port.list.unknownCarrier')
}

function TransferRow({ line, locale, onSelect }: { line: TransferLine; locale: string; onSelect: () => void }) {
  const { t } = useTranslation()
  return (
    <tr onClick={onSelect} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
            {line.number ? formatNanp(line.number.phone_number) : t('manager.numbers.port.list.noNumber')}
          </span>
          {line.number?.status === 'rejete' && (
            <span className="text-xs text-red-600 dark:text-red-400 mt-0.5">{t('manager.numbers.port.detail.rejected')}</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <TransferTypeBadge />
      </td>
      <td className="px-6 py-4 text-slate-700 dark:text-slate-300">{transferFrom(line, t)}</td>
      <td className="px-6 py-4">
        <StatusBadge status={line.transfer.status} />
      </td>
      <td className="px-6 py-4 text-slate-500 dark:text-slate-400">
        <TransferDateText transfer={line.transfer} locale={locale} />
      </td>
      <td className="px-6 py-4 text-right">
        <Button type="button" variant="ghost" size="sm" onClick={onSelect}>
          {t('manager.numbers.port.list.open')}
        </Button>
      </td>
    </tr>
  )
}

function TransferCard({ line, locale, onSelect }: { line: TransferLine; locale: string; onSelect: () => void }) {
  const { t } = useTranslation()
  return (
    <div onClick={onSelect} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
          {line.number ? formatNanp(line.number.phone_number) : t('manager.numbers.port.list.noNumber')}
        </span>
        <StatusBadge status={line.transfer.status} />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <TransferTypeBadge />
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>{transferFrom(line, t)}</span>
        <span>
          <TransferDateText transfer={line.transfer} locale={locale} />
        </span>
      </div>
    </div>
  )
}

function AssignmentBadge({ number }: { number: CompanyPhoneNumber }) {
  const { t } = useTranslation()
  const effectiveType = number.ivr_flow_id ? 'ivr_flow' : number.assignment_type

  if (effectiveType === 'main') {
    return (
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500/20">
          {t('manager.numbers.assignment.main')}
        </span>
        {number.also_used_by_ai && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 ring-1 ring-purple-500/20 ml-1">
            + {t('manager.numbers.assignment.aiAgent')}
          </span>
        )}
      </div>
    )
  }
  if (effectiveType === 'ai_agent') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 ring-1 ring-purple-500/20">
        <Bot className="h-3 w-3" />
        {t('manager.numbers.assignment.aiAgent')}
      </span>
    )
  }
  if (effectiveType === 'ivr_flow') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/20">
        <GitBranch className="h-3 w-3" />
        {t('manager.numbers.assignment.ivrFlow')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 ring-1 ring-slate-200 dark:ring-slate-600">
      {t('manager.numbers.assignment.unassigned')}
    </span>
  )
}

function OwnerCell({ number }: { number: CompanyPhoneNumber }) {
  if (!number.owner_name) {
    return <span className="text-slate-400">—</span>
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {number.owner_type === 'ai_agent' ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
            <Bot className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
            <Building2 className="h-3.5 w-3.5" />
          </span>
        )}
        <span className="text-slate-700 dark:text-slate-300">
          {number.owner_name}
        </span>
      </div>
      {number.also_used_by_ai && number.ai_agent_name && (
        <div className="flex items-center gap-2 pl-8">
          <Bot className="h-3 w-3 text-purple-500" />
          <span className="text-xs text-slate-500">
            + {number.ai_agent_name}
          </span>
        </div>
      )}
    </div>
  )
}

function CapabilitiesCell({ number }: { number: CompanyPhoneNumber }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex items-center px-2 py-1 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-xs border border-slate-200 dark:border-slate-700">
        {t('manager.numbers.detail.capability.voice')}
      </span>
      <TollfreeSmsPill number={number} />
    </div>
  )
}

interface RowActionsProps {
  number: CompanyPhoneNumber
  companyId: number
  onDetails: () => void
  onEditLabel: () => void
}

function RowActions({ number, companyId, onDetails, onEditLabel }: RowActionsProps) {
  const { t } = useTranslation()
  const update = useUpdatePhoneNumber(companyId, number.id)

  const handleUnassign = async () => {
    try {
      await update.mutateAsync({ assignment_type: 'unassigned' })
      toast.success(t('manager.numbers.detail.unassigned'))
    } catch {
      toast.error(t('manager.numbers.detail.unassignError'))
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onDetails} className="cursor-pointer">
          <Eye className="h-4 w-4 mr-2" />
          {t('manager.numbers.actions.details')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEditLabel} className="cursor-pointer">
          <Tag className="h-4 w-4 mr-2" />
          {number.label
            ? t('manager.numbers.actions.editLabel')
            : t('manager.numbers.actions.addLabel')}
        </DropdownMenuItem>
        {number.assignment_type !== 'unassigned' &&
          number.assignment_type !== 'ai_agent' && (
            <DropdownMenuItem
              onClick={() => void handleUnassign()}
              className="cursor-pointer"
            >
              <Edit className="h-4 w-4 mr-2" />
              {t('manager.numbers.actions.unassign')}
            </DropdownMenuItem>
          )}
        {number.assignment_type === 'unassigned' && (
          <DropdownMenuItem
            onClick={onDetails}
            className={cn(
              'cursor-pointer text-red-600 focus:text-red-600',
              'focus:bg-red-50 dark:focus:bg-red-500/10',
            )}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('manager.numbers.actions.delete')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
